/**
 * B.12-R8.8 — «Авторасстановка» bottom-sheet (long-press AUTO). Strategy
 * segmented control presets human weight sliders (no raw numbers); manual edit
 * → «Своя». Gender toggle + exam toggle + named presets (★default / save).
 * «Запустить расстановку» runs autoSuggest with the derived config.
 */
import { useState } from "react";
import {
  STRATEGIES, WEIGHT_LABELS, DEFAULT_WEIGHTS, weightsToConfig,
  type Weights, type WeightKey, type StrategyId,
} from "./autoSuggest";
import type { ScoringConfig } from "./scoring";
import css from "./board.module.css";

interface Preset {
  name: string;
  strategy: StrategyId;
  weights: Weights;
  useGender: boolean;
  requireExam: boolean;
  isDefault: boolean;
}

const SEED_PRESETS: Preset[] = [
  { name: "Будни — ровные зарплаты", strategy: "even", weights: STRATEGIES[2]!.weights, useGender: true, requireExam: true, isDefault: true },
  { name: "Пятница — лучшие", strategy: "best", weights: STRATEGIES[1]!.weights, useGender: true, requireExam: true, isDefault: false },
];

export function AutoSheet({
  open, onClose, onRun,
}: {
  open: boolean;
  onClose: () => void;
  onRun: (config: ScoringConfig, useGender: boolean, requireExam: boolean) => void;
}) {
  const [strategy, setStrategy] = useState<StrategyId>("even");
  const [weights, setWeights] = useState<Weights>(DEFAULT_WEIGHTS);
  const [useGender, setUseGender] = useState(true);
  const [requireExam, setRequireExam] = useState(true);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [presets, setPresets] = useState<Preset[]>(SEED_PRESETS);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");

  function pickStrategy(id: Exclude<StrategyId, "custom">) {
    setStrategy(id);
    setWeights(STRATEGIES.find((s) => s.id === id)!.weights);
  }
  function setWeight(k: WeightKey, v: number) {
    setWeights((w) => ({ ...w, [k]: v }));
    setStrategy("custom");
  }
  function applyPreset(p: Preset) {
    setStrategy(p.strategy);
    setWeights(p.weights);
    setUseGender(p.useGender);
    setRequireExam(p.requireExam);
  }
  function makeDefault(name: string) {
    setPresets((ps) => ps.map((p) => ({ ...p, isDefault: p.name === name })));
  }
  function savePreset() {
    const name = newName.trim();
    if (!name) return;
    setPresets((ps) => [...ps, { name, strategy, weights, useGender, requireExam, isDefault: false }]);
    setNewName("");
    setSaving(false);
  }

  if (!open) return null;

  return (
    <>
      <div className={css.sheetBackdrop} onClick={onClose} />
      <div className={css.autoSheet} role="dialog" aria-modal="true">
        <div className={css.asHandle} />
        <div className={css.asHead}>
          <span className={css.asTitle}>Авторасстановка</span>
          <button type="button" className={css.asClose} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>

        <div className={css.asBody}>
          <div className={css.asLbl}>Стратегия{strategy === "custom" ? <span className={css.asCustom}> · Своя</span> : null}</div>
          <div className={css.segRow}>
            {STRATEGIES.map((s) => (
              <button key={s.id} type="button" className={`${css.seg} ${strategy === s.id ? css.segOn : ""}`} onClick={() => pickStrategy(s.id)}>
                {s.label}
              </button>
            ))}
          </div>

          <button type="button" className={css.weightsToggle} onClick={() => setWeightsOpen((o) => !o)}>
            Веса {weightsOpen ? "▴" : "▾"}
          </button>
          {weightsOpen ? (
            <div className={css.weightsBox}>
              {(Object.keys(WEIGHT_LABELS) as WeightKey[]).map((k) => (
                <div key={k} className={css.weightRow}>
                  <span className={css.weightLbl}>{WEIGHT_LABELS[k]}</span>
                  <div className={css.sliderWrap}>
                    <span className={css.sliderEnd}>низкий</span>
                    <input type="range" min={0} max={100} step={10} value={weights[k]} className={css.slider}
                      onChange={(e) => setWeight(k, Number(e.target.value))} />
                    <span className={css.sliderEnd}>высокий</span>
                  </div>
                </div>
              ))}
              <label className={css.toggleRow}>
                <input type="checkbox" checked={requireExam} onChange={(e) => setRequireExam(e.target.checked)} />
                Хотя бы один со сданным экзаменом
              </label>
            </div>
          ) : null}

          <label className={css.toggleRow}>
            <input type="checkbox" checked={useGender} onChange={(e) => setUseGender(e.target.checked)} />
            Учитывать пол
          </label>

          <div className={css.asLbl}>Пресеты</div>
          <div className={css.presetList}>
            {presets.map((p) => (
              <div key={p.name} className={css.presetRow}>
                <button type="button" className={css.presetStar} onClick={() => makeDefault(p.name)} aria-label="По умолчанию">{p.isDefault ? "★" : "☆"}</button>
                <button type="button" className={css.presetName} onClick={() => applyPreset(p)}>{p.name}</button>
              </div>
            ))}
            {saving ? (
              <div className={css.presetRow}>
                <input className={css.presetInput} placeholder="Имя пресета…" value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} />
                <button type="button" className={css.presetSaveBtn} onClick={savePreset}>OK</button>
              </div>
            ) : (
              <button type="button" className={css.presetAdd} onClick={() => setSaving(true)}>＋ Сохранить как пресет</button>
            )}
          </div>
        </div>

        <div className={css.asFoot}>
          <button type="button" className={css.asRun} onClick={() => onRun(weightsToConfig(weights), useGender, requireExam)}>
            Запустить расстановку
          </button>
        </div>
      </div>
    </>
  );
}
